// Redux slice for spending analytics state management

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
  SpendingAnalyticsData, 
  SpendingAnalyticsRequest,
  InsightData,
  GenerateInsightsRequest,
  AnalyticsFilters,
  DEFAULT_ANALYTICS_FILTERS
} from '../../types/spending-analytics';

// Async thunk for fetching spending analytics
export const fetchSpendingAnalytics = createAsyncThunk(
  'spendingAnalytics/fetchSpendingAnalytics',
  async (request: SpendingAnalyticsRequest, { rejectWithValue }) => {
    try {
      const response = await fetch('/.netlify/functions/getSpendingAnalytics', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });
      
      const data = await response.json();
      
      if (!data.success) {
        return rejectWithValue(data.error || 'Failed to fetch spending analytics');
      }
      
      return data.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unexpected error occurred');
    }
  }
);

// Async thunk for generating insights
export const fetchInsights = createAsyncThunk(
  'spendingAnalytics/fetchInsights',
  async (request: GenerateInsightsRequest, { rejectWithValue }) => {
    try {
      const response = await fetch('/.netlify/functions/generateInsights', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });
      
      const data = await response.json();
      
      if (!data.success) {
        return rejectWithValue(data.error || 'Failed to generate insights');
      }
      
      return data.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'An unexpected error occurred');
    }
  }
);

interface SpendingAnalyticsState {
  // Analytics data
  analytics: SpendingAnalyticsData | null;
  insights: InsightData[];
  
  // Loading states
  isLoadingAnalytics: boolean;
  isLoadingInsights: boolean;
  
  // Error states
  analyticsError: string | null;
  insightsError: string | null;
  
  // UI state
  filters: AnalyticsFilters;
  selectedPeriod: 'week' | 'month' | 'quarter' | 'year';
  selectedView: 'overview' | 'categories' | 'trends' | 'partners' | 'insights';
  
  // Cache
  lastFetchTime: number | null;
  cacheKey: string | null;
}

const initialState: SpendingAnalyticsState = {
  analytics: null,
  insights: [],
  isLoadingAnalytics: false,
  isLoadingInsights: false,
  analyticsError: null,
  insightsError: null,
  filters: DEFAULT_ANALYTICS_FILTERS,
  selectedPeriod: 'month',
  selectedView: 'overview',
  lastFetchTime: null,
  cacheKey: null,
};

const spendingAnalyticsSlice = createSlice({
  name: 'spendingAnalytics',
  initialState,
  reducers: {
    // UI actions
    setFilters: (state, action: PayloadAction<Partial<AnalyticsFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      // Clear cache when filters change
      state.lastFetchTime = null;
      state.cacheKey = null;
    },
    
    setSelectedPeriod: (state, action: PayloadAction<'week' | 'month' | 'quarter' | 'year'>) => {
      state.selectedPeriod = action.payload;
      state.filters.period = action.payload;
      // Clear cache when period changes
      state.lastFetchTime = null;
      state.cacheKey = null;
    },
    
    setSelectedView: (state, action: PayloadAction<'overview' | 'categories' | 'trends' | 'partners' | 'insights'>) => {
      state.selectedView = action.payload;
    },
    
    // Reset state
    resetAnalytics: (state) => {
      state.analytics = null;
      state.insights = [];
      state.analyticsError = null;
      state.insightsError = null;
      state.lastFetchTime = null;
      state.cacheKey = null;
    },
    
    // Clear errors
    clearErrors: (state) => {
      state.analyticsError = null;
      state.insightsError = null;
    },
    
    // Reset filters to default
    resetFilters: (state) => {
      state.filters = DEFAULT_ANALYTICS_FILTERS;
      state.lastFetchTime = null;
      state.cacheKey = null;
    },
  },
  
  extraReducers: (builder) => {
    // Fetch spending analytics
    builder
      .addCase(fetchSpendingAnalytics.pending, (state) => {
        state.isLoadingAnalytics = true;
        state.analyticsError = null;
      })
      .addCase(fetchSpendingAnalytics.fulfilled, (state, action) => {
        state.isLoadingAnalytics = false;
        state.analytics = action.payload;
        state.analyticsError = null;
        state.lastFetchTime = Date.now();
        // Create cache key based on request parameters
        state.cacheKey = JSON.stringify({
          period: state.filters.period,
          monthsBack: state.filters.monthsBack,
          categoryIds: state.filters.categoryIds,
          partners: state.filters.partners,
        });
      })
      .addCase(fetchSpendingAnalytics.rejected, (state, action) => {
        state.isLoadingAnalytics = false;
        state.analyticsError = action.payload as string;
        state.analytics = null;
      });
    
    // Fetch insights
    builder
      .addCase(fetchInsights.pending, (state) => {
        state.isLoadingInsights = true;
        state.insightsError = null;
      })
      .addCase(fetchInsights.fulfilled, (state, action) => {
        state.isLoadingInsights = false;
        state.insights = action.payload;
        state.insightsError = null;
      })
      .addCase(fetchInsights.rejected, (state, action) => {
        state.isLoadingInsights = false;
        state.insightsError = action.payload as string;
        state.insights = [];
      });
  },
});

export const {
  setFilters,
  setSelectedPeriod,
  setSelectedView,
  resetAnalytics,
  clearErrors,
  resetFilters,
} = spendingAnalyticsSlice.actions;

export default spendingAnalyticsSlice.reducer;

// Selectors
export const selectSpendingAnalytics = (state: { spendingAnalytics: SpendingAnalyticsState }) => state.spendingAnalytics.analytics;
export const selectInsights = (state: { spendingAnalytics: SpendingAnalyticsState }) => state.spendingAnalytics.insights;
export const selectIsLoadingAnalytics = (state: { spendingAnalytics: SpendingAnalyticsState }) => state.spendingAnalytics.isLoadingAnalytics;
export const selectIsLoadingInsights = (state: { spendingAnalytics: SpendingAnalyticsState }) => state.spendingAnalytics.isLoadingInsights;
export const selectAnalyticsError = (state: { spendingAnalytics: SpendingAnalyticsState }) => state.spendingAnalytics.analyticsError;
export const selectInsightsError = (state: { spendingAnalytics: SpendingAnalyticsState }) => state.spendingAnalytics.insightsError;
export const selectAnalyticsFilters = (state: { spendingAnalytics: SpendingAnalyticsState }) => state.spendingAnalytics.filters;
export const selectSelectedPeriod = (state: { spendingAnalytics: SpendingAnalyticsState }) => state.spendingAnalytics.selectedPeriod;
export const selectSelectedView = (state: { spendingAnalytics: SpendingAnalyticsState }) => state.spendingAnalytics.selectedView;

// Cache selectors
export const selectIsCacheValid = (state: { spendingAnalytics: SpendingAnalyticsState }, maxAge: number = 5 * 60 * 1000) => {
  const { lastFetchTime } = state.spendingAnalytics;
  if (!lastFetchTime) return false;
  return Date.now() - lastFetchTime < maxAge;
};

export const selectShouldRefetch = (state: { spendingAnalytics: SpendingAnalyticsState }, currentRequest: any) => {
  const { cacheKey, lastFetchTime } = state.spendingAnalytics;
  const newCacheKey = JSON.stringify({
    period: currentRequest.period,
    monthsBack: currentRequest.months_back,
    categoryIds: currentRequest.categoryIds,
    partners: currentRequest.partners,
  });
  
  // Refetch if cache key changed or cache is old
  return cacheKey !== newCacheKey || !selectIsCacheValid(state);
};